// The commissioner log (AUDIT_LOG.md, scenario J5): every rule change and
// commissioner act, and the stat corrections that moved a result, newest
// first, with who, when and why. The sentences are the server's; this screen
// lays them out and opens each act's own page.
//
// One list at every width: the log is one question, and a second column would
// have nothing to answer. Drawn through the kit.

import { whenInZone, type AuditEntry } from '@fsp/domain';
import { useNavigate } from 'react-router-dom';
import { useAuditLog } from '../data/audit';
import { useAtLeast } from '../data/detail';
import { useLeagueBase, useReaderZone } from '../data/hooks';
import { Icon, type IconName } from '../kit/Icon';
import { Button, Caption, ListRow, RowSub, Text, Title } from '../kit/ui';
import { ListScreen } from '../templates/ListScreen';
import { FromLeague } from './shared/FromLeague';
import '../styles/screens/audit.css';

const ICONS: Record<AuditEntry['kind'], IconName> = {
  'settings.change': 'rules',
  'trade.approve': 'accept',
  'trade.veto': 'veto',
  'seat.remove': 'remove',
  'seat.close': 'close',
  'seat.invite': 'people',
  'seat.bot': 'bot',
  'seat.unbot': 'bot',
  'invite.rotate': 'invite',
  'commissioner.handover': 'crown',
  'division.set': 'people',
  'rival.set': 'people',
  'schedule.draw': 'random',
  'lineup.fix': 'edit',
  'points.adjust': 'add',
  'score.adjust': 'refresh',
  'transaction.reverse': 'undo',
  'waivers.priority': 'waivers',
  'waivers.budget': 'waivers',
  'season.complete': 'trophy',
  'season.next': 'play',
  'league.eliminated': 'remove',
  'chat.hide': 'chat',
  'chat.show': 'chat',
  other: 'info',
};

/** Where an entry's act lives, or null when it has nowhere to go. */
export function auditTarget(e: AuditEntry, base: string): { to: string; label: string } | null {
  if (e.refs.version !== null)
    return { to: `${base}/rules?section=versions`, label: 'Open the rules’ versions' };
  if (e.refs.tradeId !== null)
    return {
      to: `${base}/trades?trade=${encodeURIComponent(e.refs.tradeId)}`,
      label: 'Open the trade',
    };
  // A reversed add, drop or claim lives in the transaction log, beside the
  // move it took back (J2); a reversed trade opened above.
  if (e.kind === 'transaction.reverse')
    return { to: `${base}/waivers?show=log`, label: 'Open the transaction log' };
  // A team moved in the waiver order or its budget set (J7): the order is on Waivers.
  if (e.kind === 'waivers.priority' || e.kind === 'waivers.budget')
    return { to: `${base}/waivers`, label: 'Open Waivers' };
  if (e.refs.matchupId !== null)
    return { to: `${base}/matchup/${e.refs.matchupId}`, label: 'Open the matchup' };
  if (e.refs.teamId !== null)
    return { to: `${base}/teams/${e.refs.teamId}`, label: 'Open the team' };
  return null;
}

export function AuditScreen() {
  const log = useAuditLog();
  const zone = useReaderZone();
  const standard = useAtLeast('standard');
  const base = useLeagueBase();
  const navigate = useNavigate();

  return (
    <FromLeague snapshot={log.snapshot}>
      {(entries) => (
        <ListScreen
          filters={<Title>Commissioner log</Title>}
          header={
            <>
              <Caption>Newest first</Caption>
              <Caption>
                {log.older !== null
                  ? `latest ${entries.length}`
                  : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
              </Caption>
            </>
          }
          rows={entries}
          getKey={(e) => e.id}
          estimateSize={72}
          empty="Nothing yet. Every rule change and commissioner action will be listed here, with who did it and when."
          footer={
            log.older !== null || log.loadingOlder || log.olderFailure !== null ? (
              <div className="fsp-audit-older">
                {log.olderFailure !== null ? (
                  <Text tone="secondary" size="xs">
                    {log.olderFailure}
                  </Text>
                ) : null}
                {log.older !== null || log.loadingOlder ? (
                  <Button
                    size="xs"
                    variant="quiet"
                    disabled={log.loadingOlder}
                    onClick={() => log.older?.()}
                  >
                    {log.loadingOlder ? 'Reading…' : 'Older entries'}
                  </Button>
                ) : null}
              </div>
            ) : undefined
          }
          renderRow={(e) => {
            const target = auditTarget(e, base);
            return (
              <ListRow
                lead={<Icon name={ICONS[e.kind]} />}
                leadSize="sm"
                tall
                onClick={target === null ? undefined : () => navigate(target.to)}
              >
                <Text block>{e.sentence}</Text>
                {e.reason !== null && e.reason !== '' ? <RowSub wrap>“{e.reason}”</RowSub> : null}
                {standard && e.detail !== null ? <RowSub wrap>{e.detail}</RowSub> : null}
                <RowSub>
                  {e.actor.name}
                  {zone === null ? '' : ` · ${whenInZone(e.at, zone, Date.now())}`}
                </RowSub>
              </ListRow>
            );
          }}
        />
      )}
    </FromLeague>
  );
}
